import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  PendingDocument,
  CreateVendorRequest,
  CreateVendorResponse,
  CreatePurchaseOrderRequest,
  CreatePurchaseOrderResponse,
  VendorSearchResponse,
  PurchaseOrderSearchResponse,
  PurchaseOrderLineResponse,
  MatchDetailsResponse,
  VendorListResponse,
  PurchaseOrderListResponse,
  PendingDocumentListResponse,
  LinkMissingReferenceResponse,
  SaveExtractionDataResponse,
  ExtractionStatusResponse,
  ApproveExtractionResponse,
  RejectExtractionResponse,
  DeleteDocumentResponse,
  DocumentHeaderUpdateRequest,
  DocumentHeaderUpdateResponse,
  InvoiceAttachment,
  MissingReferenceInfo,
  CorrectionsResponse,
  ApplyDocumentFlagActionRequest,
  ApplyDocumentFlagActionResponse,
  ErpFieldValue,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';
import { useAuthStore } from '@store/authStore';

const useTenantQueryScope = () => useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

export const usePendingPOs = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['pending', tenantScope, "purchase_order", skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/pending/po', {
        params: { skip, limit },
      });
      return data;
    },
  });
};

export const usePendingReceipts = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['pending', tenantScope, 'receipts', skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/pending/receipts', {
        params: { skip, limit },
      });
      return data;
    },
  });
};

export const usePendingInvoices = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['pending', tenantScope, 'invoices', skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/pending/invoices', {
        params: { skip, limit },
      });
      return data;
    },
  });
};

export const useApprovedPOs = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['approved', tenantScope, "purchase_order", skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/approved/po', {
        params: { skip, limit },
      });
      return data;
    },
  });
};

export const useApprovedReceipts = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['approved', tenantScope, 'receipts', skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/approved/receipts', {
        params: { skip, limit },
      });
      return data;
    },
  });
};

export const useApprovedInvoices = (skip = 0, limit = 20) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['approved', tenantScope, 'invoices', skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingDocumentListResponse>('/documents/approved/invoices', {
        params: { skip, limit },
      });
      return data;
    },
  });
};


export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: CreateVendorRequest) => {
      const { data } = await apiClient.post<CreateVendorResponse>(
        '/vendors/create-from-review',
        request
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Vendor "${data.legal_name}" created successfully`,
        type: 'success',
      });
      // Invalidate pending documents to refresh missing references and vendor links
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
    },
  });
};

export const useCreatePurchaseOrder = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: CreatePurchaseOrderRequest) => {
      const { data } = await apiClient.post<CreatePurchaseOrderResponse>(
        '/purchase-orders/create-from-review',
        request
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Purchase Order "${data.po_number}" created successfully`,
        type: 'success',
      });
      // Invalidate pending documents to refresh missing references
      queryClient.invalidateQueries({ queryKey: ['pending'] });
    },
    onError: (error: any) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to create purchase order',
        type: 'error',
      });
    },
  });
};

export const useSearchVendors = (searchTerm: string, skip = 0, limit = 20, erpConnectionId?: string) => {
  const normalizedSearchTerm = searchTerm.trim();

  return useQuery({
    queryKey: ['vendors', 'search', normalizedSearchTerm, skip, limit, erpConnectionId ?? 'all-connections'],
    queryFn: async () => {
      if (!normalizedSearchTerm || normalizedSearchTerm.length < 1) {
        return { items: [], total: 0, skip, limit };
      }
      const { data } = await apiClient.get<VendorListResponse>('/vendors/search', {
        params: { search: normalizedSearchTerm, offset: skip, limit, erp_connection_id: erpConnectionId },
      });
      return data;
    },
    enabled: normalizedSearchTerm.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
};

export const useSearchPurchaseOrders = (searchTerm: string, skip = 0, limit = 20) => {
  return useQuery({
    queryKey: ['purchase-orders', 'search', searchTerm, skip, limit],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 1) {
        return { items: [], total: 0, skip, limit };
      }
      const { data } = await apiClient.get<PurchaseOrderListResponse>('/purchase-orders/search', {
        params: { search: searchTerm, offset: skip, limit },
      });
      return data;
    },
    enabled: !!searchTerm && searchTerm.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
};

export const usePurchaseOrderLines = (poId?: string | null) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['purchase-orders', tenantScope, poId, 'lines'],
    queryFn: async () => {
      const { data } = await apiClient.get<PurchaseOrderLineResponse[]>(
        `/purchase-orders/${poId}/lines`
      );
      return data;
    },
    enabled: Boolean(poId),
  });
};

export const useLinkMissingReference = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      referenceId,
      objectType,
      objectId,
    }: {
      referenceId: string;  // Public reference of the missing reference
      objectType: 'vendor' | "purchase_order";
      objectId: string;
    }) => {
      const { data } = await apiClient.post<LinkMissingReferenceResponse>(
        `documents/missing-references/${referenceId}/link`,
        { entity_type: objectType, entity_identifier: objectId }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: `Successfully linked to ${variables.objectType} ${variables.objectId}`,
        type: 'success',
      });
      // Invalidate relevant queries so linked references and document fields refresh in open review modals.
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['missing-references'] });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to link missing reference',
        type: 'error',
      });
    },
  });
};

export const useSaveExtractionData = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      extractionData,
    }: {
      documentType: "purchase_order" | 'receipt' | 'invoice';
      documentId: string;
      extractionData: Record<string, any>;
    }) => {
      const { data } = await apiClient.put<SaveExtractionDataResponse>(
        `/documents/pending/${documentType}/${documentId}/extraction-data`,
        { extraction_data: extractionData }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: 'Extracted data saved successfully',
        type: 'success',
      });
      // Invalidate pending documents to refresh extracted data
      queryClient.invalidateQueries({ queryKey: ['pending'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to save extracted data',
        type: 'error',
      });
    },
  });
};

/**
 * New Temporal workflow-aware hooks for hybrid extraction approval workflow
 */

export const useExtractionStatus = (documentType: string, documentId: string | undefined) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['extraction-status', tenantScope, documentType, documentId],
    queryFn: async () => {
      const { data } = await apiClient.get<ExtractionStatusResponse>(
        `/documents/${documentType}/${documentId}/extraction-status`
      );
      return data;
    },
    enabled: !!documentType && !!documentId,
  });
};

export const useInvoiceAttachments = (invoiceId: string | undefined, enabled = true) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['invoice-attachments', tenantScope, invoiceId],
    queryFn: async () => {
      const { data } = await apiClient.get<InvoiceAttachment[]>(
        `/documents/invoice/${invoiceId}/attachments`
      );
      return data;
    },
    enabled: enabled && Boolean(invoiceId),
  });
};

export const useUploadInvoiceAttachments = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      files,
    }: {
      invoiceId: string;
      files: File[];
    }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      const { data } = await apiClient.post<InvoiceAttachment[]>(
        `/documents/invoice/${invoiceId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Invoice attachment uploaded successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-attachments'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-attachments', variables.invoiceId] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to upload invoice attachment',
        type: 'error',
      });
    },
  });
};

export const useDeleteInvoiceAttachment = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      attachmentId,
    }: {
      invoiceId: string;
      attachmentId: string;
    }) => {
      const { data } = await apiClient.delete<{ status: string; attachment_id: string }>(
        `/documents/invoice/${invoiceId}/attachments/${attachmentId}`
      );
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Invoice attachment removed',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-attachments'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-attachments', variables.invoiceId] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to remove invoice attachment',
        type: 'error',
      });
    },
  });
};

export const useDocumentMatchDetails = (documentType: string, documentId: string | undefined) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['match-details', tenantScope, documentType, documentId],
    queryFn: async () => {
      const { data } = await apiClient.get<MatchDetailsResponse>(
        `/documents/${documentType}/${documentId}/match-details`
      );
      return data;
    },
    enabled: !!documentType && !!documentId,
  });
};

export const useUpdateDocumentHeader = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      payload,
    }: {
      documentType: "purchase_order" | 'receipt' | 'invoice';
      documentId: string;
      payload: DocumentHeaderUpdateRequest;
    }) => {
      const { data } = await apiClient.patch<DocumentHeaderUpdateResponse>(
        `/documents/${documentType}/${documentId}/header`,
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: data.message || 'Document header updated.',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['match-details'] });
      queryClient.invalidateQueries({ queryKey: ['missing-references'] });
      queryClient.invalidateQueries({ queryKey: ['approval-request'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['approved'] });
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-attachments'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update document header',
        type: 'error',
      });
    },
  });
};

export const useApproveExtraction = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      user,
      notes,
      corrections,
      dynamic_erp_fields,
      erp_connection_id,
    }: {
      documentType: string;
      documentId: string;
      user: string;
      notes?: string;
      corrections?: Record<string, any>;
      dynamic_erp_fields?: ErpFieldValue[];
      erp_connection_id?: string;
    }) => {
      const { data } = await apiClient.post<ApproveExtractionResponse>(
        `/documents/${documentType}/${documentId}/actions/approve`,
        {
          user,
          notes,
          corrections,
          dynamic_erp_fields,
          erp_connection_id,
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Document action submitted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['match-details'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to approve extraction',
        type: 'error',
      });
    },
  });
};

export const useRejectExtraction = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      user,
      reason,
    }: {
      documentType: string;
      documentId: string;
      user: string;
      reason: string;
    }) => {
      const { data } = await apiClient.post<RejectExtractionResponse>(
        `/documents/${documentType}/${documentId}/actions/discard`,
        {
          user,
          reason,
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Document discard submitted',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['match-details'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to reject extraction',
        type: 'error',
      });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      user,
      reason,
    }: {
      documentType: string;
      documentId: string;
      user: string;
      reason?: string;
    }) => {
      const { data } = await apiClient.delete<DeleteDocumentResponse>(
        `/documents/${documentType}/${documentId}`,
        {
          data: {
            user,
            reason,
          },
        }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      addNotification({
        message: 'Document deleted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['match-details'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to delete document',
        type: 'error',
      });
    },
  });
};

export const useMissingReferences = (documentType: string, documentId: string | undefined) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['missing-references', tenantScope, documentType, documentId],
    queryFn: async () => {
      const { data } = await apiClient.get<MissingReferenceInfo[]>(
        `/documents/${documentType}/${documentId}/missing-references`
      );
      return data;
    },
    enabled: !!documentType && !!documentId,
  });
};

export const useSubmitCorrections = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      user,
      corrections,
      notes,
      dynamic_erp_fields,
      erp_connection_id,
    }: {
      documentType: "purchase_order" | 'receipt' | 'invoice';
      documentId: string;
      user: string;
      corrections: Record<string, any>;
      notes?: string;
      dynamic_erp_fields?: ErpFieldValue[];
      erp_connection_id?: string;
    }) => {
      const { data } = await apiClient.post<CorrectionsResponse>(
        `/documents/${documentType}/${documentId}/actions/correct`,
        {
          user,
          corrections,
          notes,
          dynamic_erp_fields,
          erp_connection_id,
        }
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: data.message || 'Corrections submitted successfully.',
        type: 'success',
      });
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['match-details'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['approved'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to submit corrections',
        type: 'error',
      });
    },
  });
};

export const useApplyDocumentFlagAction = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      documentType,
      documentId,
      flagId,
      request,
    }: {
      documentType: "purchase_order" | 'receipt' | 'invoice';
      documentId: string;
      flagId: string;
      request: ApplyDocumentFlagActionRequest;
    }) => {
      const { data } = await apiClient.post<ApplyDocumentFlagActionResponse>(
        `/documents/${documentType}/${documentId}/flags/${flagId}/actions`,
        request
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: data.message || 'Flag action completed successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['extraction-status'] });
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to apply document flag action',
        type: 'error',
      });
    },
  });
};
